import React from 'react';
import { CheckIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format, parseISO, isAfter } from 'date-fns';
import { Task } from '../api/tasks';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface TaskViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onStatusChange: (taskId: string, newStatus: Task['status']) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
}

export default function TaskViewModal({ 
  isOpen, 
  onClose, 
  task, 
  onStatusChange, 
  onEdit, 
  onDelete 
}: TaskViewModalProps) {
  if (!task) return null;

  const isOverdue = task.status !== 'COMPLETED' && 
                    task.status !== 'CANCELLED' && 
                    isAfter(new Date(), parseISO(task.dueDate));

  const getStatusBadge = (status: Task['status']) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-cyan-800', label: 'In Progress' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      OVERDUE: { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
      DEFERRED: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Deferred' },
      ESCALATED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Escalated' }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority: Task['priority']) => {
                    const priorityConfig = {
                  LOW: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Low' },
                  MEDIUM: { bg: 'bg-blue-100', text: 'text-cyan-800', label: 'Medium' },
                  HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
                  URGENT: { bg: 'bg-red-100', text: 'text-red-800', label: 'Urgent' }
                };

    const config = priorityConfig[priority] || priorityConfig.MEDIUM;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type: Task['type']) => {
    const typeConfig = {
      BREEDING: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Breeding' },
      HEALTH_CHECK: { bg: 'bg-green-100', text: 'text-green-800', label: 'Health Check' },
      BANDING: { bg: 'bg-blue-100', text: 'text-cyan-800', label: 'Banding' },
      FEEDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Feeding' },
      MAINTENANCE: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Maintenance' },
      GENERAL: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'General' },
      OTHER: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Other' }
    };

    const config = typeConfig[type] || typeConfig.OTHER;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleStatusChange = (newStatus: Task['status']) => {
    onStatusChange(task.id, newStatus);
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Task Details" onClose={onClose} />
      <BrandModalBody className="space-y-6">
                  {/* Header with badges */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-slate-900 mb-2">
                        {task.title}
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {getTypeBadge(task.type)}
                        {getPriorityBadge(task.priority)}
                        {getStatusBadge(task.status)}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {task.description && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-2">Description</h3>
                      <p className="text-sm text-slate-600 bg-slate-50/80 p-3 rounded-md">
                        {task.description}
                      </p>
                    </div>
                  )}

                  {/* Key Information */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-2">Due Date</h3>
                      <div className={`flex items-center text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-900'}`}>
                        <ClockIcon className="mr-2 h-4 w-4" />
                        {format(parseISO(task.dueDate), 'MMM dd, yyyy')}
                        {isOverdue && (
                          <ExclamationTriangleIcon className="ml-2 h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-2">Assigned To</h3>
                      <p className="text-sm text-slate-900">
                        {task.assignedTo || 'Unassigned'}
                      </p>
                    </div>

                    {task.completedDate && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-700 mb-2">Completed Date</h3>
                        <p className="text-sm text-slate-900">
                          {format(parseISO(task.completedDate), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    )}

                    {task.relatedEntityType && task.relatedEntityId && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-700 mb-2">Related Entity</h3>
                        <p className="text-sm text-slate-900">
                          {task.relatedEntityType}: {task.relatedEntityId}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Metadata */}
                  {task.metadata && Object.keys(task.metadata).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-2">Additional Information</h3>
                      <div className="bg-slate-50/80 p-3 rounded-md">
                        {task.metadata.notes && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-slate-700">Notes: </span>
                            <span className="text-sm text-slate-600">{task.metadata.notes}</span>
                          </div>
                        )}
                        {task.metadata.estimatedDuration && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-slate-700">Estimated Duration: </span>
                            <span className="text-sm text-slate-600">{task.metadata.estimatedDuration} minutes</span>
                          </div>
                        )}
                        {task.metadata.actualDuration && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-slate-700">Actual Duration: </span>
                            <span className="text-sm text-slate-600">{task.metadata.actualDuration} minutes</span>
                          </div>
                        )}
                        {task.metadata.tags && task.metadata.tags.length > 0 && (
                          <div>
                            <span className="text-sm font-medium text-slate-700">Tags: </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {task.metadata.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Status Management */}
                  {task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-3">Update Status</h3>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleStatusChange('IN_PROGRESS')}
                          disabled={task.status === 'IN_PROGRESS'}
                          className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
                            task.status === 'IN_PROGRESS'
                              ? 'bg-cyan-100 text-cyan-800 cursor-not-allowed'
                              : 'bg-cyan-600 text-white hover:bg-cyan-700'
                          }`}
                        >
                          <ClockIcon className="mr-2 h-4 w-4" />
                          Start Work
                        </button>
                        <button
                          onClick={() => handleStatusChange('COMPLETED')}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
                        >
                          <CheckIcon className="mr-2 h-4 w-4" />
                          Mark Complete
                        </button>
                        <button
                          onClick={() => handleStatusChange('CANCELLED')}
                          className="btn btn-secondary text-sm"
                        >
                          Cancel Task
                        </button>
                      </div>
                    </div>
                  )}

      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary text-sm"
        >
          Close
        </button>
        <button
          type="button"
          onClick={() => onEdit(task)}
          className="btn btn-primary text-sm"
        >
          Edit Task
        </button>
        <button
          type="button"
          onClick={() => onDelete(task)}
          className="btn btn-danger text-sm"
        >
          Delete Task
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 